import {
  deleteAdmin,
  deleteUser,
  isAdmin as isAdminType,
  sendBanEmail,
  unbanUser,
  updateStudentPromoPercentage,
  useAdminMe,
  useAppUsers,
  useBoUsers,
  usePaymentDefaultUsers,
  useStudentReduction,
  useStudentUsers,
  validateStudentStatus,
} from '@gozoki/api'
import { includesLowerCase } from '@gozoki/tools'
import { Box, Button, InputAdornment, Paper, Tab, Tabs, TextField, Typography } from '@mui/material'
import type { GridColDef } from '@mui/x-data-grid'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import Page from '../../../components/Page'
import { ConfirmButton } from '../../../components/communication/ConfirmButton'
import { ConfirmResourceDeletionButton } from '../../../components/communication/ConfirmDeletionButton'
import { useToast } from '../../../components/communication/Toast'
import SimpleDataGrid from '../../../components/containers/SimpleDataGrid'
import PlusIcon from '../../../components/icons/PlusIcon'
import { UserRolesCell } from '../../../components/routes/users/UserRolesCell'
import { useRights } from '../../../utils/hooks'
import { getErrorMessageWithSentry } from '../../../utils/sentry'
import { fonts } from '../../../utils/theme/fonts.theme'

import { withResourcePermission } from '../../../components/RequireResourcePermission'
export const Route = createFileRoute('/_authenticated/users/')({
  component: withResourcePermission('users', 'read')(UsersPage),
})

interface ValidateUserStatus {
  userId: number
  status: boolean
}

const usersColumnDefs = (
  currentTab: number,
  sendEmail: (id: number) => Promise<unknown>,
  handleValidateStudentStatus: null | ((u: ValidateUserStatus) => void),
  handleUnban?: (id: number) => Promise<unknown>,
): GridColDef[] => {
  if (currentTab === 3) {
    // This is admin mode
    return [
      {
        field: 'firstName',
        headerName: 'Prénom',
        width: 140,
      },
      {
        field: 'lastName',
        headerName: 'Nom',
        width: 140,
      },
      {
        field: 'store',
        headerName: 'Boutique',
        valueGetter: (params) => params.row.stores?.[0]?.label ?? '',
        width: 140,
      },
      { field: 'email', headerName: 'Email', width: 300 },
      {
        field: 'adminRights',
        flex: 1,
        headerName: 'Roles',
        minWidth: 200,
        renderCell: UserRolesCell,
      },
      {
        field: 'id',
        headerName: 'ID',
        type: 'number',
        width: 70,
      },
    ]
  }
  if (currentTab === 1) {
    // This is student mode
    return [
      {
        field: 'firstName',
        headerName: 'Prénom',
        width: 140,
      },
      {
        field: 'lastName',
        headerName: 'Nom',
        width: 140,
      },
      { field: 'email', headerName: 'Email', width: 300 },
      {
        field: 'store',
        headerName: 'Boutique',
        valueGetter: (params) => params.row.store?.label ?? '',
        width: 140,
      },
      {
        field: 'studentDocumentRectoUrl',
        flex: 1,
        headerName: 'Voir le justificatif (recto)',
        minWidth: 200,
        renderCell: (params) => (
          <Box>
            {params.value ? (
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => {
                  window.open(params.value as string, '_blank')
                }}
              >
                Voir
              </Button>
            ) : (
              <Box>-</Box>
            )}
          </Box>
        ),
      },
      {
        field: 'studentDocumentVersoUrl',
        flex: 1,
        headerName: 'Voir le justificatif (verso)',
        minWidth: 200,
        renderCell: (params) => (
          <Box>
            {params.row.studentDocumentVersoUrl ? (
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => {
                  window.open(params.row.studentDocumentVersoUrl as string, '_blank')
                }}
              >
                Voir
              </Button>
            ) : (
              <Box>-</Box>
            )}
          </Box>
        ),
      },
      {
        field: 'isValidatedStudent',
        headerName: 'Statut',
        minWidth: 200,
        renderCell: (params) =>
          params.row.isValidatedStudent === null
            ? 'En attente'
            : `${params.row.isValidatedStudent ? 'Validé' : 'Non validé'}`,
      },
      {
        field: '_isStudent1',
        headerName: '',
        renderCell: (params) =>
          params.row.isValidatedStudent !== true ? (
            <Button
              variant="contained"
              color="success"
              disabled={!handleValidateStudentStatus}
              onClick={() => {
                handleValidateStudentStatus?.({
                  userId: params.row.id,
                  status: true,
                })
              }}
            >
              Valider
            </Button>
          ) : (
            <p> - </p>
          ),
      },
      {
        field: '_isStudent2',
        headerName: '',
        renderCell: (params) =>
          params.row.isValidatedStudent !== false ? (
            <Button
              variant="contained"
              color="error"
              disabled={!handleValidateStudentStatus}
              onClick={() => {
                handleValidateStudentStatus?.({
                  userId: params.row.id,
                  status: false,
                })
              }}
            >
              Invalider
            </Button>
          ) : (
            <p> - </p>
          ),
      },
      {
        field: 'id',
        headerName: 'ID',
        type: 'number',
      },
    ]
  }
  if (currentTab === 2) {
    // This is default payment mode
    return [
      {
        field: 'firstName',
        headerName: 'Prénom',
        width: 140,
      },
      {
        field: 'lastName',
        headerName: 'Nom',
        width: 140,
      },
      { field: 'email', headerName: 'Email', width: 300 },
      {
        field: 'store',
        headerName: 'Boutique',
        valueGetter: (params) => params.row.store?.label ?? '',
        width: 140,
      },
      {
        field: 'unBanAmount',
        headerName: 'Montant',
        type: 'number',
        width: 150,
      },
      {
        field: 'id_2',
        headerName: 'Actions',
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: 1 }} onClick={(e) => e.stopPropagation()}>
            <ConfirmButton
              buttonText="Envoyer un email"
              buttonProps={{
                variant: 'contained',
                disabled: params.row.isBanned === false,
              }}
              title="Envoyer un email de relance"
              description={`Voulez-vous envoyer un email de relance à ${params.row.firstName} ${params.row.lastName} ?`}
              onConfirm={async () => sendEmail(params.row.id as number)}
            />
            <ConfirmButton
              buttonText="Débannir"
              buttonProps={{
                variant: 'contained',
                sx: { backgroundColor: '#ffa400', '&:hover': { backgroundColor: '#e69300' } },
                disabled: params.row.isBanned === false || !handleUnban,
              }}
              title="Débannir l'utilisateur"
              description={`Voulez-vous débannir ${params.row.firstName} ${params.row.lastName} ? Son montant de débannissement (${params.row.unBanAmount ?? 0}€) sera annulé.`}
              onConfirm={async () => handleUnban?.(params.row.id as number)}
            />
          </Box>
        ),
        width: 380,
      },
    ]
  }
  if (currentTab === 0) {
    // This is the default mode : app users
    return [
      {
        field: 'firstName',
        headerName: 'Prénom',
        width: 140,
      },
      {
        field: 'lastName',
        headerName: 'Nom',
        width: 140,
      },
      { field: 'email', headerName: 'Email', width: 300 },
      {
        field: 'store',
        headerName: 'Boutique',
        valueGetter: (params) => params.row.store?.label ?? '',
        width: 140,
      },
      {
        field: 'isBanned',
        headerName: 'Banni',
        type: 'boolean',
      },
      {
        field: 'isReliable',
        headerName: 'Est solvable',
        type: 'boolean',
      },
      {
        field: 'payzenPaymentTokens',
        headerName: 'Cartes de paiments',
        type: 'number',
        valueFormatter: (params) => params.value.length,
        width: 140,
      },
      {
        field: 'payzenConecsPaymentTokens',
        headerName: 'Cartes conecs',
        type: 'number',
        valueFormatter: (params) => params.value.length,
        width: 140,
      },
      {
        field: 'id',
        headerName: 'ID',
        type: 'number',
        width: 70,
      },
    ]
  }
  return []
}

function UsersPage() {
  const navigate = useNavigate()
  const isUserSuperAdmin = useRights(['SUPER_ADMIN'])
  const isAdmin = useRights(['ADMIN'])

  const [userSearch, setUserSearch] = useState('')
  const [userSelected, setUserSelected] = useState<number[]>([])
  const [currentTab, setCurrentTab] = useState(0)
  const [studentPromoPercentage, setStudentPromoPercentage] = useState(0)

  const me = useAdminMe()
  const [Toast, showToast] = useToast()

  const { data: appUsers, refetch: refetchAppUsers, isLoading: isLoadingAppUsers } = useAppUsers()

  const {
    data: studentUsers,
    refetch: refetchStudentUsers,
    isLoading: isLoadingStudentUsers,
  } = useStudentUsers()

  const { data: boUsers, refetch: refetchBoUsers, isLoading: isLoadingBoUsers } = useBoUsers()

  const {
    data: paymentDefaultUSers,
    refetch: refetchPaymentDefaultUsers,
    isLoading: isLoadingPaymentDefaultUsers,
  } = usePaymentDefaultUsers()

  const {
    data: studentPromoPercentageData,
    refetch: refetchStudentPromoPercentage,
    isLoading: isLoadingStudentPromoPercentage,
  } = useStudentReduction()

  useEffect(() => {
    setStudentPromoPercentage(studentPromoPercentageData ?? 0)
  }, [studentPromoPercentageData])

  const isLoading = useMemo(() => {
    if (currentTab === 0) {
      return isLoadingAppUsers
    }
    if (currentTab === 1) {
      return isLoadingStudentUsers || isLoadingStudentPromoPercentage
    }
    if (currentTab === 2) {
      return isLoadingPaymentDefaultUsers
    }
    if (currentTab === 3) {
      return isLoadingBoUsers
    }

    return false
  }, [
    currentTab,
    isLoadingAppUsers,
    isLoadingBoUsers,
    isLoadingPaymentDefaultUsers,
    isLoadingStudentPromoPercentage,
    isLoadingStudentUsers,
  ])

  const refreshUsers = useCallback(() => {
    if (currentTab === 0) {
      return refetchAppUsers()
    }
    if (currentTab === 1) {
      return refetchStudentUsers()
    }
    if (currentTab === 2) {
      return refetchPaymentDefaultUsers()
    }
    if (currentTab === 3) {
      return refetchBoUsers()
    }

    return Promise.resolve()
  }, [currentTab, refetchAppUsers, refetchBoUsers, refetchPaymentDefaultUsers, refetchStudentUsers])

  const { mutate: mutateUserStatus } = useMutation({
    mutationFn: ({ userId, status }: { userId: number; status: boolean }) =>
      validateStudentStatus(userId, status),
    onError: (error) => {
      showToast({ message: getErrorMessageWithSentry(error), severity: 'error' })
    },
    onSuccess: () => {
      showToast({
        message: "Statut de l'étudiant modifié",
        severity: 'success',
      })
      refreshUsers()
    },
  })

  const handleValidatedStudentStatus = isAdmin
    ? (u: ValidateUserStatus) => {
        mutateUserStatus({ status: u.status, userId: u.userId })
      }
    : null

  // ****************************************************** //
  //           QUERY TO DATAGRID TRANSFORMATION             //
  // ****************************************************** //

  const users = useMemo(() => {
    if (currentTab === 0) {
      return appUsers ?? []
    }
    if (currentTab === 1) {
      return studentUsers ?? []
    }
    if (currentTab === 2) {
      return paymentDefaultUSers ?? []
    }
    if (currentTab === 3) {
      return boUsers ?? []
    }

    return []
  }, [appUsers, boUsers, currentTab, paymentDefaultUSers, studentUsers])

  const userRows = useMemo(() => {
    if (!users) {
      return []
    }
    return users.filter((user) =>
      includesLowerCase(`${user.firstName} ${user.lastName} ${user.email}`, userSearch),
    )
  }, [userSearch, users])

  const { mutate: deleteOneUser } = useMutation({
    mutationFn: (id: number) => deleteUser(id),
    onSuccess: () => {
      showToast({
        message: 'Utilisateur supprimé',
        severity: 'success',
      })
    },
    onError: (error) => {
      showToast({ message: getErrorMessageWithSentry(error), severity: 'error' })
    },
  })

  const { mutate: deleteOneAdmin } = useMutation({
    mutationFn: (id: number) => deleteAdmin(id),
    onSuccess: () => {
      showToast({
        message: 'Utilisateur supprimé',
        severity: 'success',
      })
      refreshUsers()
    },
    onError: (error) => {
      showToast({ message: getErrorMessageWithSentry(error), severity: 'error' })
    },
  })

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue)
  }

  const { mutate: mutateUpdateStudentPromoPercentage } = useMutation({
    mutationFn: updateStudentPromoPercentage,
    onSuccess: async () => {
      await refetchStudentPromoPercentage()
      showToast({
        message: 'Promo étudiant mise à jour',
        severity: 'success',
      })
    },
    onError: (error) => {
      showToast({ message: getErrorMessageWithSentry(error), severity: 'error' })
    },
  })

  const handleDelete = async () =>
    new Promise((resolve, reject) => {
      const usersTodelete = userSelected.map((i) => users.find((user) => user.id === i))
      // avoid deleting the current user, an admin or a CPI user
      if (
        usersTodelete?.some(
          (user) => user?.id === me.data?.id || (!isUserSuperAdmin && isAdminType(user)),
        )
      ) {
        showToast({
          message: 'Impossible de supprimer un utilisateur avec droits particuliers',
          severity: 'error',
        })
        reject(new Error('Impossible de supprimer un utilisateur avec droits particuliers'))
      } else {
        Promise.all(
          usersTodelete?.map((user) => {
            if (!user) return Promise.resolve()
            if (isAdminType(user)) return Promise.resolve(deleteOneAdmin(user.id))
            return Promise.resolve(deleteOneUser(user.id))
          }) ?? [],
        )
          .then(() => {
            setUserSelected([])
            refreshUsers().then(() => resolve(true))
          })
          .catch((error) => {
            showToast({
              message: getErrorMessageWithSentry(error),
              severity: 'error',
            })
            reject(error)
          })
      }
    })

  const sendEmailMut = useMutation({
    mutationFn: (id: number) => sendBanEmail(id),
    onError: (error) => {
      showToast({ message: getErrorMessageWithSentry(error), severity: 'error' })
    },
    onSuccess: () => {
      showToast({
        message: 'Email envoyé',
        severity: 'success',
      })
    },
  })

  const unbanMut = useMutation({
    mutationFn: (id: number) => unbanUser(id),
    onError: (error) => {
      showToast({ message: getErrorMessageWithSentry(error), severity: 'error' })
    },
    onSuccess: () => {
      showToast({
        message: 'Utilisateur débanni',
        severity: 'success',
      })
      refreshUsers()
    },
  })

  return (
    <Page>
      <Toast />
      <div style={{ display: 'flex' }}>
        <Typography style={fonts.pageTitle} flexGrow={1}>
          Utilisateurs
        </Typography>
        <Button
          variant="contained"
          sx={{ marginRight: '16px' }}
          onClick={() =>
            navigate({
              search: { type: currentTab === 3 ? 'admin' : 'app' },
              to: '/users/new',
            })
          }
          disabled={!isUserSuperAdmin}
        >
          <PlusIcon />
          Ajouter un utilisateur
        </Button>
        <Button
          variant="contained"
          sx={{ marginRight: '16px' }}
          onClick={() =>
            navigate({
              search: {
                userId: userSelected[0],
                type: currentTab === 3 ? 'admin' : 'app',
              },
              to: '/users/new',
            })
          }
          disabled={!isUserSuperAdmin || userSelected.length !== 1}
        >
          Modifier
        </Button>
        {isAdmin ? (
          <ConfirmResourceDeletionButton
            disabled={userSelected.length === 0}
            handleResourceDeletion={handleDelete}
          />
        ) : null}
      </div>
      <Tabs value={currentTab} onChange={handleChange}>
        <Tab label="Utilisateurs de l'app" />
        <Tab label="Étudiants" />
        <Tab label="Défauts de paiement" />
        {isUserSuperAdmin ? <Tab label="Utilisateurs du BO" /> : null}
      </Tabs>
      {isUserSuperAdmin && currentTab === 1 ? (
        <Paper
          sx={{
            marginTop: '16px',
          }}
        >
          <div style={{ alignItems: 'center', display: 'flex', padding: '16px' }}>
            <TextField
              variant="outlined"
              label="Remise étudiante"
              value={studentPromoPercentage}
              onChange={(e) => setStudentPromoPercentage(Number(e.target.value))}
              type="number"
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
            <div style={{ flexGrow: 1 }} />
            <Button
              variant="contained"
              onClick={() => mutateUpdateStudentPromoPercentage(studentPromoPercentage)}
              disabled={isLoadingStudentPromoPercentage}
              sx={{ padding: 3 }}
            >
              Modifier le pourcentage de réduction
            </Button>
          </div>
        </Paper>
      ) : null}
      <Paper
        sx={{
          marginTop: '16px',
        }}
      >
        <div style={{ alignItems: 'center', display: 'flex', padding: '16px' }}>
          <TextField
            variant="outlined"
            label="Recherche"
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
          />
          <div style={{ flexGrow: 1 }} />
        </div>
        <SimpleDataGrid
          columns={usersColumnDefs(
            currentTab,
            sendEmailMut.mutateAsync,
            handleValidatedStudentStatus,
            unbanMut.mutateAsync,
          )}
          rows={userRows}
          loading={isLoading}
          checkboxSelection={currentTab !== 1}
          onRowSelectionModelChange={(newSelection) => setUserSelected(newSelection as number[])}
          disableRowSelectionOnClick={currentTab === 1}
        />
      </Paper>
    </Page>
  )
}
